import { Dropdown, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	getFolderPath,
	listFolderItems,
} from "../../services/api-server/welldocs";
import { getTenantStore } from "../../stores/tenantStore";
import { alertMessage } from "../../utils/alert";
import { GetAntIcon } from "../../utils/ant_icons";
import fileTools from "../../utils/fileTools";
import { getSharePointErrorMessage } from "../../utils/sharepointErrors";
import {
	parseFolderParam,
	withoutFolderParam,
} from "../../utils/sharepointLinks";
import SearchButton from "../SearchButton";
import { renderTableCheckboxFilter } from "../Table/TableCheckboxFilter";
import { ColumnsType } from "antd/es/table";

const EMPTY_TEXT = "No documents have been uploaded for this report";
const NO_MATCH_TEXT = "No matching documents";

/** Case-insensitive "contains" check; an empty search term matches everything. */
const matchesSearch = (text: string | undefined, term: string | undefined) => {
	const needle = term?.trim().toLowerCase();
	return !needle || (text ?? "").toLowerCase().includes(needle);
};

interface ReportingDocumentSectionProps {
	record: any;
	type:
		"audit" | "lesson_learned" | "incident" | "moc" | "non_conformance" | "ofi";
}

export function ReportingDocumentSection(props: ReportingDocumentSectionProps) {
	const { record: activeRecord, type } = props;
	const location = useLocation();
	const navigate = useNavigate();

	const tenantStore = getTenantStore();
	const tenantList = tenantStore.getTenantList();

	const [folderItems, setFolderItems] = useState<any>([]);
	const [folderHierarchy, setFolderHierarchy] = useState<any>([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	// Column searches apply to the folder on screen. Loading a folder clears
	// them, and the new searchKey remounts the header inputs so they clear too.
	const [searchValues, setSearchValues] = useState<Record<string, string>>({});
	const [searchKey, setSearchKey] = useState(0);

	const recordOperator = activeRecord?.operator;
	const recordId = activeRecord?._id;

	/** Lists a folder into the table; resolves false when it could not be read. */
	const loadFolder = useCallback(
		(folderId: string, containerId: string): Promise<boolean> => {
			setLoading(true);
			setLoadError(null);
			setSearchValues({});
			setSearchKey((key) => key + 1);
			return listFolderItems(
				recordOperator,
				recordId,
				folderId,
				containerId,
				"x",
				type
			)
				.then((response: any) => {
					setFolderItems(response?.items || []);
					return true;
				})
				.catch((error: any) => {
					const message = getSharePointErrorMessage(error);
					setFolderItems([]);
					setLoadError(message);
					alertMessage(
						"error",
						"Unable to load the report documents.",
						message
					);
					return false;
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[recordOperator, recordId, type]
	);

	const dropdownItemOptions: any = (fileType: string, record: any) => {
		if (fileType == "file") {
			return [
				{
					label: "Preview",
					key: "0",
					onClick: () => {
						const _record = encodeURIComponent(record.id);
						const _container = encodeURIComponent(
							tenantList.find(
								(tenant: any) => tenant.role_key === props.record.operator
							)?.container_id
						);
						const new_path = `${location.pathname}/preview?record=${_record}&container=${_container}`;
						window.open(new_path, "_blank");
					},
				},
				{
					label: "Download",
					key: "2",
					onClick: () => {
						if (record[`@microsoft.graph.downloadUrl`]) {
							window.open(record[`@microsoft.graph.downloadUrl`], "_self");
						}
					},
				},
			];
		} else {
			return [];
		}
	};

	const filterFileTypeOptions: any = () => {
		let filteredData = [...folderItems];
		let arrayTypes: any = filteredData.map((record: any) => {
			if (record.folder) {
				return "File folder";
			} else if (record.file) {
				return record.file.mimeType;
			}
		});
		const uniqueArray = arrayTypes.filter(
			(item: any, index: any) => arrayTypes.indexOf(item) === index
		);
		return uniqueArray.map((element: any) => ({
			text: fileTools.getReadableFileType(element) || element,
			value: element,
		}));
	};

	/** Opens a folder linked from a file preview, or Contents when it is not in this report's folder. */
	const openLinkedFolder = (
		linkedFolderId: string,
		rootFolderId: string,
		containerId: string
	) => {
		// Show the table as loading, not empty, while the link is checked.
		setLoading(true);
		getFolderPath(recordOperator, linkedFolderId, rootFolderId)
			.then(({ folders, item }) =>
				loadFolder(linkedFolderId, containerId).then((loaded) => {
					if (loaded) setFolderHierarchy([...folders, item]);
				})
			)
			.catch(() => {
				setFolderHierarchy([]);
				loadFolder(rootFolderId, containerId);
			});
	};

	useEffect(() => {
		const folderId = activeRecord?.folder_id;
		const container_id = tenantList.find(
			(tenant: any) => tenant.role_key === activeRecord?.operator
		)?.container_id;

		// A report without a folder has nothing to list; it is not an error.
		if (!folderId || !container_id) {
			setFolderItems([]);
			setFolderHierarchy([]);
			setLoadError(null);
			return;
		}

		// Honour a deep link once, then drop it so switching tabs, which
		// remounts this section, starts from Contents again.
		const linkedFolderIds = parseFolderParam(location.search);
		if (linkedFolderIds) {
			navigate(
				{ search: withoutFolderParam(location.search) },
				{ replace: true }
			);
		}
		const linkedFolderId = linkedFolderIds?.[linkedFolderIds.length - 1];
		if (linkedFolderId && linkedFolderId !== folderId) {
			openLinkedFolder(linkedFolderId, folderId, container_id);
			return;
		}

		setFolderHierarchy([]);
		loadFolder(folderId, container_id);
	}, [tenantList, activeRecord?.folder_id, activeRecord?.operator, loadFolder]);

	const handleFolderChange = (
		folderID: any,
		folderName: string,
		index: any = null,
		setHierarchy: boolean = true,
		tenant: any = null
	) => {
		loadFolder(folderID, tenant?.container_id).then((loaded) => {
			if (!loaded || !setHierarchy) return;
			if (index == null) {
				setFolderHierarchy([
					...folderHierarchy,
					{ id: folderID, name: folderName },
				]);
			} else if (index == -1) {
				setFolderHierarchy([]);
			} else {
				setFolderHierarchy([...folderHierarchy].slice(0, index + 1));
			}
		});
	};

	const breadcrumbRenderer = () => {
		if (folderHierarchy.length > 0) {
			return (
				<>
					<a
						className="document-breadcrumb-clickable-text"
						onClick={() => {
							const tenant = tenantList.find(
								(tenant: any) => tenant.role_key === activeRecord.operator
							);
							handleFolderChange(activeRecord?.folder_id, "", -1, true, tenant);
						}}
					>
						Contents
					</a>
					{folderHierarchy.map((folder: any, index: number) => {
						if (index === folderHierarchy.length - 1) {
							return (
								<>
									<span className="document-breadcrumb-seperator">/</span>
									<span className="document-breadcrumb-text">
										{folder.name || "undefined"}
									</span>
								</>
							);
						} else {
							return (
								<>
									<span className="document-breadcrumb-seperator">/</span>
									<a
										className="document-breadcrumb-clickable-text"
										onClick={() => {
											const tenant = tenantList.find(
												(tenant: any) =>
													tenant.role_key === activeRecord.operator
											);
											handleFolderChange(
												folder.id,
												folder.name,
												index,
												true,
												tenant
											);
										}}
									>
										{folder.name || "undefined"}
									</a>
								</>
							);
						}
					})}
				</>
			);
		} else {
			return <span>Contents</span>;
		}
	};

	const handleSearch = ({
		columnKey,
		value,
	}: {
		columnKey: string;
		value: string;
	}) => {
		setSearchValues((prev) => ({ ...prev, [columnKey]: value }));
	};

	/** The uploader's name, taken from the report's upload records. */
	const getAuthorName = (item: any): string | undefined =>
		activeRecord?.file_upload?.find((file: any) => file.file_name === item.name)
			?.authorName;

	const searchedItems = folderItems.filter(
		(item: any) =>
			matchesSearch(item.name, searchValues.name) &&
			matchesSearch(getAuthorName(item), searchValues.owner)
	);

	const columns: ColumnsType<any> = [
		{
			title: (
				<SearchButton
					handleSearch={handleSearch}
					headerName="Name"
					columnKey="name"
					key={`name-${searchKey}`}
				/>
			),
			dataIndex: "name",
			key: "name",
			width: 200,
			render: (value: any, record: any, index: any) => {
				let fileType: string = record.file
					? "file"
					: record.folder
						? "folder"
						: "";

				let prefix = record.name;

				return (
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							gap: "10px",
							width: "100%",
						}}
					>
						<a
							className="table-clickable-text"
							style={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								gap: "10px",
								flex: 1,
								minWidth: 0,
							}}
							onClick={() => {
								const tenant = tenantList.find(
									(tenant: any) => tenant.role_key === activeRecord.operator
								);
								if (record.folder) {
									handleFolderChange(
										record.id,
										record.name,
										null,
										true,
										tenant
									);
								} else if (record.file) {
									const _record = encodeURIComponent(record.id);
									const container_id = tenant?.container_id;
									if (!container_id) return;
									const _container = encodeURIComponent(container_id);
									const new_path = `${location.pathname}/preview?record=${_record}&container=${_container}`;
									window.open(new_path, "_blank");
								}
							}}
						>
							<span className="table-clickable-icon-text">
								{record.folder
									? GetAntIcon("folder-outline")
									: GetAntIcon("file2")}{" "}
							</span>
							<Tooltip title={prefix}>
								<span
									style={{
										flex: 1,
										minWidth: 0,
										maxWidth: 200,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{prefix}
								</span>
							</Tooltip>
						</a>

						<Dropdown
							disabled={false}
							menu={{
								items: dropdownItemOptions(
									fileType,
									record,
									index,
									activeRecord
								),
							}}
							trigger={["click"]}
						>
							<a onClick={(e) => e.preventDefault()}>
								{GetAntIcon("ellipsis")}
							</a>
						</Dropdown>
					</div>
				);
			},
		},
		{
			title: (
				<SearchButton
					handleSearch={handleSearch}
					headerName="Added by"
					columnKey="owner"
					key={`owner-${searchKey}`}
				/>
			),
			dataIndex: "owner",
			key: "owner",
			render: (_: any, record: any) => getAuthorName(record) || "-",
		},
		{
			title: "Added on",
			dataIndex: "createdDateTime",
			key: "creation_date",

			render: (value: any) => (
				<span>{value && dayjs(value)?.format("D MMM YYYY HH:mm:ss")}</span>
			),
			sorter: (a: any, b: any) => {
				const stringA = a.createdDateTime || "";
				const stringB = b.createdDateTime || "";
				return stringA.localeCompare(stringB);
			},
		},
		// {
		// 	title: "Last modified",
		// 	dataIndex: "lastModifiedDateTime",
		// 	key: "last_modified_date",
		// 	render: (value: any) => (
		// 		<span>
		// 			{value && dayjs(value)?.format("D MMM YYYY hh:mm:ss A")}
		// 		</span>
		// 	),
		// 	sorter: (a: any, b: any) => {
		// 		const stringA = a.lastModifiedDateTime || "";
		// 		const stringB = b.lastModifiedDateTime || "";
		// 		return stringA.localeCompare(stringB);
		// 	},
		// },
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			filters: filterFileTypeOptions(),
			filterDropdown: renderTableCheckboxFilter,
			onFilter: (value: any, record: any) => {
				return (
					record.file?.mimeType == value ||
					(record.folder && value === "File folder")
				);
			},
			render: (value: any, record: any) => {
				if (record.folder) {
					return <span>File folder</span>;
				} else if (record.file) {
					return (
						<span>{fileTools.getReadableFileType(record.file.mimeType)}</span>
					);
				}
			},
		},
		{
			title: "Size",
			dataIndex: "size",
			key: "size",
			sorter: (a: any, b: any) => a.size - b.size,
			render: (value: any) => {
				// let renderedValue = (value / 1000000).toFixed(4);
				// return <span>{value && renderedValue}</span>;

				return <span>{fileTools.formatFileSize(value)}</span>;
			},
		},
	];

	return (
		<div>
			<div className="document-container-header">
				<div id={"documentBreadcrumb"} className="document-breadcrumb mb-4">
					{breadcrumbRenderer()}
				</div>
			</div>
			<Table
				tableLayout="auto"
				columns={columns}
				dataSource={searchedItems}
				loading={loading}
				locale={{
					emptyText:
						loadError ?? (folderItems.length > 0 ? NO_MATCH_TEXT : EMPTY_TEXT),
				}}
				scroll={{ x: "max-content" }}
				rootClassName="custom-table"
			/>
		</div>
	);
}

export default ReportingDocumentSection;
